/**
 * DevRoleSwitcher — role override banner for local development.
 *
 * Renders in any Vite dev build (devRoleOverrideEnabled).
 * Real MSAL auth is unaffected; API calls still carry real JWT tokens.
 * The selected role overrides only what the frontend UI displays
 * (RequireRole, useCurrentUser, nav items).
 *
 * "auto" clears the override and reverts to the role from the real token.
 */

import { makeStyles, tokens, Select, Label } from '@fluentui/react-components';
import { useState } from 'react';
import {
  devRoleOverrideEnabled,
  getDevRoleOverride,
  setDevRoleOverride,
  type DevRole,
} from '@/config/devAuth';

const useStyles = makeStyles({
  banner: {
    position: 'fixed',
    bottom: '0',
    right: '0',
    zIndex: '9999',
    display: 'flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalS,
    paddingTop: tokens.spacingVerticalXS,
    paddingBottom: tokens.spacingVerticalXS,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    backgroundColor: tokens.colorPaletteYellowBackground2,
    borderTopLeftRadius: tokens.borderRadiusMedium,
    borderTopRightRadius: tokens.borderRadiusMedium,
    boxShadow: tokens.shadow8,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  label: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase200,
    whiteSpace: 'nowrap',
  },
});

type OverrideOption = DevRole | 'auto';
const OPTIONS: OverrideOption[] = ['auto', 'external', 'reviewer', 'admin'];

export function DevRoleSwitcher() {
  const styles = useStyles();
  const [value, setValue] = useState<OverrideOption>(
    () => getDevRoleOverride() ?? 'auto'
  );

  if (!devRoleOverrideEnabled) return null;

  function handleChange(next: string) {
    const option = next as OverrideOption;
    setDevRoleOverride(option === 'auto' ? null : option);
    setValue(option);
    window.location.reload();
  }

  return (
    <div className={styles.banner}>
      <Label className={styles.label} htmlFor="dev-role-select">
        DEV ROLE:
      </Label>
      <Select
        id="dev-role-select"
        size="small"
        value={value}
        onChange={(_e, data) => handleChange(data.value)}
      >
        {OPTIONS.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </Select>
    </div>
  );
}

export default DevRoleSwitcher;
